/** The recorder engine (decision 0051; docs/recorder-api.md): captures the microphone and (online) the call sound as two
 * tracks on one clock, encrypts every chunk into the device buffer, uploads with the recording's own bearer token, and
 * never drops audio silently. Framework-free: the screens call it and render what its events say. */
import { createTrackCapture } from "./capture.js";
import type { CaptureOptions, Chunk, TrackCapture } from "./capture.js";
import { httpRecorderApi } from "./api.js";
import { aadFor, encryptChunk, importKey } from "./crypto.js";
import { defaultStore } from "./store.js";
import type { ChunkMeta, ChunkStore, RecordingMeta } from "./store.js";
import { Uploader } from "./uploader.js";
import type { QueuedChunk } from "./uploader.js";
import { ApiFailure } from "./types.js";
import type {
  EngineEvents, EngineState, RecorderApi, RecoverableRecording, ResumeResponse, Started, StopResponse, StopResult, Streams, Track, Unsent, Warning,
} from "./types.js";

/** What kind of share the call track is: a tab, the entire screen, or (never usable) a window. The browser's report of
 * what was actually shared wins over the route the screen offered; with no report, the route decides. */
export type CallKind = "tab" | "screen" | "window";
export function callKindOf(surface: string | null | undefined, route: "tab" | "screen" | null | undefined): CallKind | null {
  if (surface === "monitor") return "screen";
  if (surface === "browser") return "tab";
  if (surface === "window") return "window";
  return route ?? null;
}

/** Sharing again may only swap one source for the same kind (tab for tab, screen for screen). Anything else changes what
 * the consent covered, so it needs Stop and a new recording with fresh consent. */
export class ReshareKindError extends Error {
  constructor(readonly was: CallKind | null, readonly now: CallKind | null) {
    super("That is a different kind of share. Stop this recording and start a new one, with a new consent tick.");
  }
}

/** `recover` found the server has ended the recording (its key is deleted: discarded, cancelled, expired or quarantined). The
 * audio left on this device could never be read, so it has been removed. */
export class RecordingEndedError extends Error {
  constructor(readonly code: string) {
    super("That recording is no longer on the server, so the audio left on this computer was removed.");
  }
}

export interface EngineOptions {
  api?: RecorderApi;
  store?: ChunkStore;
  getCsrf?: () => string | Promise<string>;
  callSilentSeconds?: number;
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
  makeCapture?: (o: CaptureOptions) => TrackCapture;
  audioContext?: () => AudioContext;
  timers?: boolean; // false in tests: they call tick() themselves
}

const SOUND = 0.02;
// The launch cap on one recording and the warning before it, when the server does not say (it does: Started.recording_limit_seconds).
const DEFAULT_MAX_RECORDING_S = 55 * 60;
const DEFAULT_WARN_AT_S = 50 * 60;
// Chunks per track the server allows for a recovered recording (no start answer to read it from): the cap plus the 60 s a clean stop needs, in 10 s chunks. Keep in step with MAX_CHUNKS_PER_TRACK in services/meetings/recorder.py.
const DEFAULT_MAX_CHUNKS_PER_TRACK = (DEFAULT_MAX_RECORDING_S + 60) / 10;
const MEMORY_CHUNK_LIMIT = 30; // chunks held in memory after the device refused writes: 5 minutes, then the recording pauses
const UNSENT_INFO_S = 30;
const UNSENT_WARN_S = 120;
const UNSENT_ERROR_S = 600;
// CAP-009 Pause/Continue (A s.21; D's conditions): before the server closes chunk-url, wait up to this long for what is
// already recorded to upload; anything left waits on this device. 20 pauses per recording at most.
const PAUSE_FLUSH_MS = 20_000;
const MAX_PAUSES = 20;
const PAUSE_EXPIRED_MESSAGE = "The recording was paused for more than 30 minutes, so it has ended. Start a new recording.";

/** A pause that can't happen (the 20th already used, or the recording isn't running). Nothing was stopped. */
export class PauseRefusedError extends Error {
  constructor(readonly code: string, message: string) {
    super(message);
  }
}

export class RecorderEngine {
  private state: EngineState = "idle";
  private readonly api: RecorderApi;
  private readonly store: ChunkStore;
  private readonly now: () => number;
  private readonly sleep: (ms: number) => Promise<void>;
  private readonly make: (o: CaptureOptions) => TrackCapture;
  private readonly silentSeconds: number;
  private readonly useTimers: boolean;

  private started: Started | null = null;
  private key: CryptoKey | null = null;
  private token = "";
  private uploader: Uploader | null = null;
  private ctx: AudioContext | null = null;
  private captures: Partial<Record<Track, TrackCapture>> = {};
  private counts: Record<Track, number> = { mic: 0, call: 0 };
  private offsets: Partial<Record<Track, number>> = {};
  private pending: Promise<void>[] = [];
  private memoryChunks = 0;
  private t0 = 0;
  private timer: ReturnType<typeof setInterval> | null = null;
  private lastSoundAt = 0;
  private silentWarned = false;
  private callEnded = false;
  private pausedWarned = false;
  private callRoute: "tab" | "screen" | null = null;
  private callSurface: string | null = null;
  private callKind: CallKind | null = null;
  private lastUnsentLevel: "none" | "info" | "warn" | "error" = "none";
  private expiringWarned = false;
  private limitWarned = false;
  private limitReached = false;
  private storagePaused = false;
  private signedOutWaiters: Array<() => void> = [];
  private background: Promise<StopResult> | null = null;
  private manifestDeclared: Partial<Record<Track, number>> | null = null;
  // Pause / Continue (CAP-009.H11b)
  private mime = "";
  private chunkMs = 10_000;
  private tracks: Track[] = [];
  private pausedAt: number | null = null; // this.now() when the pause began
  private pausedTotalMs = 0;
  private pauseExpiresAt = 0; // Date.now() ms the server ends the pause
  private pauses = 0;
  private pauseUnsaved = false;

  constructor(private readonly events: EngineEvents, private readonly opts: EngineOptions = {}) {
    this.api = opts.api ?? httpRecorderApi(opts.getCsrf ?? (async () => ""));
    this.store = opts.store ?? defaultStore().store;
    this.now = opts.now ?? (() => performance.now());
    this.sleep = opts.sleep ?? ((ms) => new Promise((r) => setTimeout(r, ms)));
    this.make = opts.makeCapture ?? createTrackCapture;
    this.silentSeconds = opts.callSilentSeconds ?? 120;
    this.useTimers = opts.timers !== false;
  }

  getState(): EngineState {
    return this.state;
  }

  private setState(s: EngineState): void {
    if (this.state !== s) {
      this.state = s;
      this.events.onState(s);
    }
  }

  // ---- start -------------------------------------------------------------------------------

  /** `info` says which kind of share the call track is (the route the screen offered, and what the browser reported), so
   * that sharing again can be held to the same kind. The client is not recorded on the device. */
  async start(streams: Streams, started: Started, info: { call_source?: "tab" | "screen" | null; call_surface?: string | null } = {}): Promise<void> {
    if (this.state !== "idle") throw new Error("The engine is already running.");
    this.setState("starting");
    this.callRoute = info.call_source ?? null;
    this.callSurface = info.call_surface ?? null;
    this.callKind = callKindOf(this.callSurface, this.callRoute);
    this.started = started;
    this.token = started.token;
    this.key = await importKey(started.key);
    const tracks: Track[] = streams.call ? ["mic", "call"] : ["mic"];
    const meta: RecordingMeta = {
      recording_id: started.recording_id, host: started.host, meeting_id: started.meeting_id,
      started_at: new Date().toISOString(), expires_at: started.unsent_expires_at, tracks,
      chunk_seconds: started.chunk_seconds, mime_type: "audio/webm",
    };
    try {
      await this.store.putMeta(meta);
    } catch (e) {
      this.events.onWarning({ kind: "storage_error", detail: errName(e) });
    }
    this.uploader = this.makeUploader(started.recording_id);
    this.ctx = this.opts.audioContext ? this.opts.audioContext() : new AudioContext();
    void this.ctx.resume?.();
    this.mime = pickMime();
    this.chunkMs = started.chunk_seconds * 1000;
    this.tracks = tracks;
    try {
      this.makeCaptures(streams);
    } catch (e) {
      this.setState("error");
      this.events.onError("not_audio_only", (e as Error).message);
      throw e;
    }
    this.t0 = this.now();
    for (const t of tracks) {
      this.offsets[t] = Math.round(this.now() - this.t0);
      this.captures[t]!.start(this.t0);
    }
    this.lastSoundAt = this.now();
    this.setState("recording");
    if (this.useTimers) this.timer = setInterval(() => this.tick(), 250);
  }

  /** The captures for these streams; after a pause their chunks number on from the earlier ones. */
  private makeCaptures(streams: Streams): void {
    const opts = (track: Track, stream: MediaStream) => ({
      track, stream, ctx: this.ctx!, mimeType: this.mime, chunkMs: this.chunkMs, onChunk: (c: Chunk) => this.accept(c), now: this.now, firstSeq: this.counts[track],
    });
    this.captures = {};
    this.captures.mic = this.make(opts("mic", streams.mic));
    if (streams.call) {
      this.captures.call = this.make(opts("call", streams.call));
      this.captures.call.onEnded(() => {
        this.callEnded = true;
        this.events.onWarning({ kind: "call_stopped" });
      });
    }
  }

  // ---- pause and continue (CAP-009.H11b; A s.21; D's conditions) --------------------------------

  /** Pauses the recording: flushes the last chunk, stops capturing and releases the microphone and the call share (the
   * device's mic indicator goes off), lets what's recorded upload, then tells the server. Nothing is captured until
   * the person taps Continue. */
  async pause(): Promise<void> {
    if (this.state !== "recording") throw new PauseRefusedError("not_recording", "Only a running recording can be paused.");
    if (this.pauses >= MAX_PAUSES) throw new PauseRefusedError("too_many_pauses", "This recording has been paused 20 times, the most it can be.");
    this.setState("pausing");
    for (const c of Object.values(this.captures)) await c!.stop(); // the last chunk, then the tracks are stopped
    await Promise.all(this.pending);
    await Promise.race([this.uploader!.drained(), this.sleep(PAUSE_FLUSH_MS)]);
    this.pauses += 1;
    this.pausedAt = this.now();
    const audioMs = Math.max(0, Math.round(this.pausedAt - this.t0 - this.pausedTotalMs));
    try {
      const r = await this.api.pause(this.token, this.started!.recording_id, { audio_ms: audioMs });
      this.pauseExpiresAt = Date.parse(r.pause_expires_at);
    } catch (e) {
      // Already capturing nothing, which is what matters. The server didn't record the pause (it couldn't be reached, or
      // said no): keep the pause here, end it after 30 minutes here too, and keep asking the server with the same
      // audio_ms in the background until it records it or Continue is tapped (D), so the gap still reaches the notes.
      this.pauseExpiresAt = Date.now() + 30 * 60_000;
      if (e instanceof ApiFailure && e.code === "cap_reached") {
        this.setState("paused");
        void this.stop();
        return;
      }
      this.pauseUnsaved = true;
      this.setState("paused");
      this.events.onWarning({ kind: "pause_unsaved" });
      void this.retryPause(audioMs);
      return;
    }
    this.setState("paused");
  }

  private async retryPause(audioMs: number): Promise<void> {
    const backoff = [2000, 5000, 10_000, 20_000, 30_000];
    for (let i = 0; this.state === "paused" && this.pauseUnsaved; i++) {
      await this.sleep(backoff[Math.min(i, backoff.length - 1)]!);
      if (this.state !== "paused" || !this.pauseUnsaved) return;
      try {
        const r = await this.api.pause(this.token, this.started!.recording_id, { audio_ms: audioMs });
        this.pauseExpiresAt = Math.min(this.pauseExpiresAt, Date.parse(r.pause_expires_at));
        this.pauseUnsaved = false;
        this.events.onWarning({ kind: "pause_saved" });
        return;
      } catch (e) {
        // already_paused: an earlier try did land; the server has it.
        if (e instanceof ApiFailure && e.code === "already_paused") {
          this.pauseUnsaved = false;
          this.events.onWarning({ kind: "pause_saved" });
          return;
        }
        if (e instanceof ApiFailure && (e.code === "cap_reached" || e.code === "not_recording")) {
          this.pauseUnsaved = false;
          void this.stop();
          return;
        }
      }
    }
  }

  /** False while the server hasn't recorded this pause yet (the screen says "Paused (not yet saved)"). */
  pauseSaved(): boolean {
    return !this.pauseUnsaved;
  }

  /** Continue after a pause: only from the person's own tap, with fresh microphone (and, online, the same kind of call
   * share) streams. Refused once the pause has expired, which ends the recording. */
  async continueRecording(streams: Streams, info: { call_surface?: string | null } = {}): Promise<void> {
    if (this.state !== "paused") throw new Error("The recording isn't paused.");
    const stopAll = () => {
      for (const st of [streams.mic, streams.call]) for (const t of st?.getTracks?.() ?? []) t.stop();
    };
    if (Date.now() >= this.pauseExpiresAt) {
      stopAll();
      await this.endPause();
      throw new PauseRefusedError("pause_expired", PAUSE_EXPIRED_MESSAGE);
    }
    if (this.tracks.includes("call")) {
      if (!streams.call) {
        stopAll();
        throw new Error("Share the call sound again to continue.");
      }
      const next = callKindOf(info.call_surface ?? null, this.callRoute);
      if ((info.call_surface == null && this.callSurface != null) || next !== this.callKind) {
        stopAll();
        const err = new ReshareKindError(this.callKind, next);
        this.events.onError("reshare_kind", err.message);
        throw err;
      }
    }
    try {
      await this.api.continueRecording(this.token, this.started!.recording_id);
    } catch (e) {
      if (e instanceof ApiFailure && e.code === "pause_expired") {
        stopAll();
        await this.endPause();
        throw new PauseRefusedError("pause_expired", PAUSE_EXPIRED_MESSAGE);
      }
      // not_paused: the server never recorded this pause (see pause()); carry on.
      if (!(e instanceof ApiFailure && e.code === "not_paused")) {
        stopAll();
        throw e;
      }
    }
    this.pauseUnsaved = false; // Continue ends the background retry
    this.makeCaptures({ mic: streams.mic, call: this.tracks.includes("call") ? streams.call : null });
    this.pausedTotalMs += this.now() - (this.pausedAt ?? this.now());
    this.pausedAt = null;
    for (const t of this.tracks) this.captures[t]!.start(this.t0);
    this.callEnded = false;
    this.silentWarned = false;
    this.lastSoundAt = this.now();
    this.setState("recording");
  }

  /** The pause ended the recording (30 minutes; or a lock, sleep or reload while paused, which the screen treats as the
   * end): declare what was recorded so the server processes it, and say so. */
  async endPause(): Promise<StopResult> {
    if (this.state !== "paused") return { status: "awaiting_upload" };
    this.events.onWarning({ kind: "pause_expired" });
    return this.stop().catch(async (e) => {
      // 409 pause_expired: the server already ended it (and shredded the key): what's left here can never be read.
      if (e instanceof ApiFailure && e.code === "pause_expired") {
        this.uploader?.stop();
        try {
          await this.store.deleteRecording(this.started!.recording_id);
        } catch {
          /* nothing more to keep */
        }
        this.events.onUnsent(null);
        this.setState("done");
        return { status: "complete" as const };
      }
      throw e;
    });
  }

  isPaused(): boolean {
    return this.state === "paused";
  }

  pausedSince(): number | null {
    return this.pausedAt;
  }

  /** The accountant shared again after the call track ended: the recorder keeps running on the same destination. */
  replaceCall(stream: MediaStream, surface: string | null = null): void {
    const call = this.captures.call;
    if (!call) throw new Error("This recording has no call track.");
    // `surface` is the new share's displaySurface, read before its video is dropped. If the browser reports nothing for the
    // new share but did report one for the first, it cannot be confirmed to be the same kind, so it is refused.
    const next = callKindOf(surface, this.callRoute);
    const unverifiable = surface == null && this.callSurface != null;
    if (unverifiable || next !== this.callKind) {
      for (const t of stream.getTracks?.() ?? []) t.stop();
      const err = new ReshareKindError(this.callKind, next);
      this.events.onError("reshare_kind", err.message);
      throw err;
    }
    call.replaceStream(stream);
    this.callEnded = false;
    this.silentWarned = false;
    this.lastSoundAt = this.now();
  }

  private makeUploader(recordingId: string): Uploader {
    return new Uploader(this.api, this.store, this.key!, this.token, recordingId, {
      now: () => Date.now(),
      sleep: this.sleep,
      onSignedOut: () => {
        this.events.onWarning({ kind: "signed_out" });
      },
      onRefused: (code) => {
        // Not a failure: the chunk stays on the device and is retried, with a growing pause, for the whole 72 hours.
        if (this.pausedWarned) return;
        this.pausedWarned = true;
        this.events.onWarning({ kind: "uploads_paused", code });
      },
      onStored: () => {
        this.pausedWarned = false;
        this.publishUnsent();
      },
    });
  }

  // ---- chunks -----------------------------------------------------------------------------

  private accept(chunk: Chunk): void {
    this.pending.push(this.handle(chunk));
  }

  private async handle(chunk: Chunk): Promise<void> {
    const rid = this.started!.recording_id;
    const plain = new Uint8Array(await chunk.data.arrayBuffer());
    this.counts[chunk.track] = Math.max(this.counts[chunk.track], chunk.seq + 1);
    const meta: ChunkMeta = {
      recording_id: rid, track: chunk.track, seq: chunk.seq, started_ms: chunk.started_ms, duration_ms: chunk.duration_ms,
      content_type: chunk.content_type, bytes: plain.length,
    };
    const item: QueuedChunk = { meta, enqueuedAt: Date.now(), attempts: 0 };
    try {
      const cipher = await encryptChunk(this.key!, aadFor(rid, chunk.track, chunk.seq), plain);
      await this.store.putChunk(meta, cipher);
    } catch (e) {
      // The device refused the write. Never drop audio: hold the chunk in memory, say so, and pause the recording
      // before memory fills.
      item.plain = plain;
      this.memoryChunks += 1;
      this.events.onWarning({ kind: "storage_error", detail: errName(e) });
      if (this.memoryChunks >= MEMORY_CHUNK_LIMIT && !this.storagePaused) {
        this.storagePaused = true;
        this.events.onError("storage_full", "This device cannot hold more audio. Recording is paused until the audio uploads.");
      }
    }
    this.uploader!.enqueue(item);
    this.publishUnsent();
  }

  // ---- the clock ---------------------------------------------------------------------------

  /** Called every 250 ms by a timer, or by tests. */
  tick(): void {
    if (!this.started) return;
    const nowMs = this.now();
    const elapsed = Math.max(0, nowMs - this.t0);
    this.events.onElapsed(elapsed);
    const mic = this.captures.mic?.level() ?? 0;
    const call = this.captures.call ? this.captures.call.level() : null;
    this.events.onLevels({ mic, call });
    if (call !== null) {
      if (call > SOUND) {
        this.lastSoundAt = nowMs;
        this.silentWarned = false;
      } else if (!this.callEnded && !this.silentWarned && (nowMs - this.lastSoundAt) / 1000 >= this.silentSeconds) {
        this.silentWarned = true;
        this.events.onWarning({ kind: "call_silent", seconds: this.silentSeconds });
      }
    }
    this.publishUnsent();
    // The cap on one recording (55 minutes at launch, from the server; a warning five minutes before). The server refuses chunks past it, so the
    // engine stops the recording itself rather than let the end of a meeting be lost.
    const maxMs = (this.started.recording_limit_seconds ?? DEFAULT_MAX_RECORDING_S) * 1000;
    const warnMs = (this.started.recording_warn_seconds ?? DEFAULT_WARN_AT_S) * 1000;
    if (this.state === "recording" && !this.limitWarned && elapsed >= warnMs && elapsed < maxMs) {
      this.limitWarned = true;
      this.events.onWarning({ kind: "recording_limit", seconds_left: Math.max(0, Math.ceil((maxMs - elapsed) / 1000)) });
    }
    if ((this.state === "recording" || this.state === "paused") && !this.limitReached && elapsed >= maxMs) {
      // Wall-clock from the start, pauses included (D #261): a pause never extends a recording.
      this.limitReached = true;
      this.events.onWarning({ kind: "recording_limit_reached" });
      void this.stop();
    }
    if (this.state === "paused" && this.pauseExpiresAt > 0 && Date.now() >= this.pauseExpiresAt) void this.endPause();
    if (!this.expiringWarned && Date.parse(this.started.unsent_expires_at) - Date.now() <= 24 * 3600 * 1000) {
      this.expiringWarned = true;
      this.events.onWarning({ kind: "expiring", expires_at: this.started.unsent_expires_at });
    }
  }

  private publishUnsent(): void {
    if (!this.uploader || !this.started) return;
    const unsent = this.uploader.summary(this.started.unsent_expires_at, Date.now());
    this.events.onUnsent(unsent);
    if (!unsent) {
      this.lastUnsentLevel = "none";
      return;
    }
    const level = unsent.oldest_age_s > UNSENT_ERROR_S ? "error" : unsent.oldest_age_s > UNSENT_WARN_S ? "warn" : unsent.oldest_age_s > UNSENT_INFO_S ? "info" : "none";
    if (level !== "none" && level !== this.lastUnsentLevel) {
      this.lastUnsentLevel = level;
      this.events.onWarning({ kind: "unsent", level, unsent, cause: this.uploader.lastCause });
    }
  }

  // ---- stop and the uploads after it --------------------------------------------------------

  async stop(): Promise<StopResult> {
    if (this.state !== "recording" && this.state !== "paused") return this.background ? this.background : { status: "awaiting_upload" };
    const wasPaused = this.state === "paused";
    this.setState("stopping");
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    if (!wasPaused) for (const c of Object.values(this.captures)) await c!.stop(); // a pause already stopped them
    await Promise.all(this.pending);
    const manifest: Partial<Record<Track, number>> = {};
    for (const t of Object.keys(this.captures) as Track[]) manifest[t] = this.counts[t];
    this.manifestDeclared = manifest;
    const endedMs = Math.round(this.now() - this.t0);
    try {
      void this.ctx?.close?.();
    } catch {
      /* already closed */
    }
    return this.declare(manifest, endedMs);
  }

  /** Writes the manifest, then keeps uploading until the server says it is complete. Returns once the first answer is in. */
  private async declare(manifest: Partial<Record<Track, number>>, endedMs: number): Promise<StopResult> {
    this.setState("uploading");
    const first = await this.callStop(manifest, endedMs);
    if (first.status !== "awaiting_upload") return this.settle(first);
    this.background = this.finishUploads(manifest);
    return { status: "awaiting_upload" };
  }

  private async callStop(manifest: Partial<Record<Track, number>>, endedMs: number): Promise<StopResponse> {
    for (;;) {
      try {
        return await this.api.stop(this.token, this.started!.recording_id, { manifest, ended_ms: endedMs, track_offsets_ms: this.offsets });
      } catch (e) {
        if (e instanceof ApiFailure && e.status === 401) {
          this.events.onWarning({ kind: "signed_out" });
          await new Promise<void>((resolve) => this.signedOutWaiters.push(resolve));
        } else if (e instanceof ApiFailure && e.status >= 400 && e.status < 500) {
          this.events.onError(e.code, e.message);
          throw e;
        } else {
          await this.sleep(2000);
        }
      }
    }
  }

  private async finishUploads(manifest: Partial<Record<Track, number>>): Promise<StopResult> {
    for (;;) {
      await this.uploader!.drained();
      try {
        const res = await this.api.complete(this.token, this.started!.recording_id);
        if (res.status !== "awaiting_upload") return this.settle(res);
        await this.requeue(res);
      } catch (e) {
        if (e instanceof ApiFailure && e.status === 401) {
          this.events.onWarning({ kind: "signed_out" });
          await new Promise<void>((resolve) => this.signedOutWaiters.push(resolve));
        } else if (e instanceof ApiFailure && e.status >= 400 && e.status < 500) {
          this.events.onError(e.code, e.message);
          this.setState("error");
          return { status: "awaiting_upload" };
        } else {
          await this.sleep(2000);
        }
      }
      void manifest;
    }
  }

  /** The server lists chunks it still lacks: queue any copy still on the device. */
  private async requeue(res: StopResponse): Promise<void> {
    const local = await this.store.listChunks(this.started!.recording_id);
    for (const track of Object.keys(res.missing) as Track[]) {
      for (const seq of res.missing[track] ?? []) {
        const meta = local.find((c) => c.track === track && c.seq === seq);
        if (meta) this.uploader!.enqueue({ meta, enqueuedAt: Date.now(), attempts: 0 });
      }
    }
    if (this.uploader!.pending() === 0) await this.sleep(5000);
  }

  private async settle(res: StopResponse): Promise<StopResult> {
    this.uploader?.stop();
    this.events.onUnsent(null);
    if (res.status === "quarantined") {
      this.setState("quarantined");
      try {
        await this.store.deleteRecording(this.started!.recording_id);
      } catch {
        /* nothing more to keep */
      }
      return { status: "quarantined" };
    }
    try {
      await this.store.deleteRecording(this.started!.recording_id);
    } catch {
      /* the chunks were already deleted as they uploaded */
    }
    this.setState("done");
    return { status: "complete" };
  }

  // ---- discard, sign-in, recovery -----------------------------------------------------------

  async discard(opts: { keepUploaded: boolean }): Promise<void> {
    const id = this.started?.recording_id;
    if (!id) throw new Error("There is no recording to discard.");
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    for (const c of Object.values(this.captures)) await c!.stop();
    await this.api.discard(id, opts.keepUploaded);
    this.uploader?.stop();
    await this.store.deleteRecording(id); // the server's copy of the key is gone, so any copy left would be unreadable anyway
    this.events.onUnsent(null);
    this.setState("done");
  }

  async resumeAfterSignIn(): Promise<void> {
    const id = this.started!.recording_id;
    const r: ResumeResponse = await this.api.resume(id);
    this.token = r.token;
    this.key = await importKey(r.key);
    this.uploader!.setToken(r.token, this.key);
    this.signedOutWaiters.splice(0).forEach((resolve) => resolve());
  }

  /** Recordings found on this device that still hold audio, for the "N minutes not yet uploaded" banner on every open. */
  static async recoverable(store: ChunkStore = defaultStore().store, api?: RecorderApi): Promise<RecoverableRecording[]> {
    const { me } = await RecorderEngine.tidyWith(store, api);
    const out: RecoverableRecording[] = [];
    for (const meta of await store.listMetas()) {
      // On a shared computer another host's buffer is not offered, and not counted, for this host (it is left alone, not deleted).
      if (me !== null && meta.host !== undefined && meta.host !== me) continue;
      const chunks = await store.listChunks(meta.recording_id);
      if (chunks.length === 0) continue;
      const secs = (track: Track) => chunks.filter((c) => c.track === track).reduce((n, c) => n + c.duration_ms, 0) / 1000;
      const oldest = Math.min(...chunks.map((c) => Date.parse(meta.started_at) + c.started_ms));
      out.push({
        recording_id: meta.recording_id,
        started_at: meta.started_at,
        unsent: {
          minutes: Math.round((Math.max(secs("mic"), secs("call")) / 60) * 10) / 10,
          chunks: chunks.length,
          oldest_age_s: Math.max(0, Math.round((Date.now() - oldest) / 1000)),
          expires_at: meta.expires_at,
          warn_24h: Date.parse(meta.expires_at) - Date.now() <= 24 * 3600 * 1000,
        },
      });
    }
    return out;
  }

  /** Housekeeping that runs on every page open (`recoverable` calls it first). Encrypted audio is kept only for recordings the
   * server still holds open, and it is only ever cleared for the host who is signed in now:
   *  - anyone's audio past its own expiry time (its key is long gone, so it is unreadable and only takes space);
   *  - this host's recordings that the server no longer lists as open (expired by the 72 hour job, cancelled or discarded
   *    elsewhere). If the server cannot be asked (signed out, offline) this step is skipped.
   * Another host's buffer on a shared computer is never touched before its expiry, and neither is a buffer with no owner
   * marker (written by an earlier build). Metadata from an earlier build is rewritten without the client name. */
  static async tidy(store: ChunkStore = defaultStore().store, api?: RecorderApi, now: number = Date.now()): Promise<string[]> {
    return (await RecorderEngine.tidyWith(store, api, now)).removed;
  }

  private static async tidyWith(store: ChunkStore, api?: RecorderApi, now: number = Date.now()): Promise<{ removed: string[]; me: string | null }> {
    let open: Set<string> | null = null;
    let me: string | null = null;
    try {
      const list = await (api ?? httpRecorderApi(async () => "")).unsent();
      if (list && Array.isArray(list.recordings) && typeof list.host === "string" && list.host) {
        open = new Set(list.recordings.map((r) => r.recording_id));
        me = list.host;
      }
    } catch {
      /* cannot be asked: apply the local expiry only */
    }
    const removed: string[] = [];
    for (const meta of await store.listMetas()) {
      const expired = Date.parse(meta.expires_at) <= now;
      const gone = open !== null && me !== null && meta.host === me && !open.has(meta.recording_id);
      if (expired || gone) {
        try {
          await store.deleteRecording(meta.recording_id);
          removed.push(meta.recording_id);
        } catch {
          /* tried again at the next open */
        }
      } else if ("client_slug" in (meta as object)) {
        const { client_slug: _drop, ...rest } = meta as RecordingMeta & { client_slug?: unknown };
        void _drop;
        await store.putMeta(rest);
      }
    }
    return { removed, me };
  }

  /** After a reload or a closed tab: get a fresh token and key (the host is signed in), decrypt what waits, and upload it. */
  static async recover(recordingId: string, events: EngineEvents, opts: EngineOptions = {}): Promise<RecorderEngine> {
    const engine = new RecorderEngine(events, opts);
    const meta = (await engine.store.listMetas()).find((m) => m.recording_id === recordingId);
    if (!meta) throw new Error("That recording is not on this device.");
    engine.setState("starting");
    let r: ResumeResponse;
    try {
      r = await engine.api.resume(recordingId);
    } catch (e) {
      engine.setState("idle");
      // Only the owner gets a 409 (anyone else gets a 404, which says nothing about the recording and removes nothing).
      if (e instanceof ApiFailure && e.status === 409 && (e.code === "key_shredded" || e.code === "not_open")) {
        try {
          await engine.store.deleteRecording(recordingId);
        } catch {
          /* tried again at the next open */
        }
        throw new RecordingEndedError(e.code);
      }
      throw e;
    }
    engine.token = r.token;
    engine.key = await importKey(r.key);
    engine.started = {
      recording_id: recordingId, meeting_id: meta.meeting_id, token: r.token, token_expires_at: r.token_expires_at, key: "",
      max_chunks_per_track: DEFAULT_MAX_CHUNKS_PER_TRACK, chunk_seconds: meta.chunk_seconds, recording_ends_at: r.unsent_expires_at, unsent_expires_at: meta.expires_at, host: meta.host ?? "",
    };
    engine.uploader = engine.makeUploader(recordingId);
    engine.setState("uploading");
    const chunks = await engine.store.listChunks(recordingId);
    for (const t of meta.tracks) {
      const mine = chunks.filter((c) => c.track === t).sort((a, b) => a.seq - b.seq);
      engine.counts[t] = Math.max(mine.length ? mine[mine.length - 1]!.seq + 1 : 0, r.stored[t] ?? 0);
      for (const c of mine) engine.uploader.enqueue({ meta: c, enqueuedAt: Date.now() - 31_000, attempts: 0 });
    }
    // A crash left no manifest: the resumed page declares it from what it holds plus what the server already stored.
    const manifest = r.manifest ?? { ...engine.counts };
    engine.manifestDeclared = manifest;
    engine.captures = {};
    engine.background = (async () => {
      const first = await engine.callStop(manifest, 0);
      if (first.status !== "awaiting_upload") return engine.settle(first);
      return engine.finishUploads(manifest);
    })();
    return engine;
  }

  /** Posts the allow-listed diagnostics with the engine's *current* token (it changes at every resume, so a caller holding
   * the token from Start would be refused after a sign-in). Never throws: diagnostics must not disturb a recording. */
  async sendDiagnostics(body: Record<string, unknown>): Promise<void> {
    if (!this.started) return;
    try {
      await this.api.diagnostics(this.token, this.started.recording_id, body);
    } catch {
      /* best effort */
    }
  }

  /** Whether this engine is still working on the recording (uploading after Stop, or paused signed out). A second engine
   * must not be started for it: a resume revokes the first engine's token. */
  busyWith(recordingId: string): boolean {
    return this.started?.recording_id === recordingId && this.state !== "done" && this.state !== "idle" && this.state !== "error" && this.state !== "quarantined";
  }

  /** Stops the timers and the upload loops (the page is closing, or a test is over). Buffered audio stays on the device. */
  shutdown(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.uploader?.stop();
    this.signedOutWaiters.splice(0).forEach((resolve) => resolve());
  }

  /** Resolves with the final result once the uploads after Stop finish (for tests and the done screen). */
  async finished(): Promise<StopResult> {
    return this.background ?? { status: this.state === "done" ? "complete" : "awaiting_upload" };
  }
}

function errName(e: unknown): string {
  return e instanceof Error ? e.name : "Error";
}

function pickMime(): string {
  for (const m of ["audio/webm;codecs=opus", "audio/mp4", "audio/ogg;codecs=opus", "audio/webm"]) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(m)) return m;
  }
  return "audio/webm;codecs=opus";
}
